/**
 * Page 5 — CSV Cybersecurity Domain Ratings.
 *
 * Layout:
 *   • Top split (≈ 45% of page):
 *      - LEFT  → Cyber Resilience Overview (band pill + 4-segment bar +
 *                2-3 line summary). Reuses the Page-3 Adequate/Strong/etc.
 *                color system. No new visual styles introduced.
 *      - RIGHT → Key Strengths (max 5) + Key Areas for Improvement (max 5)
 *   • Bottom (~55%): "CSV Cybersecurity Domain Ratings"
 *      - 2 × 4 grid of domain cards
 *      - Each card: domain name, maturity pill, 4-segment bar, and full
 *        commentary with no truncation / ellipsis
 *
 * Data sources (no hardcoding):
 *   • Overall band              → resolved upstream (executive override or
 *                                  NIST function average)
 *   • Overall summary line      → overallSummaryLine() from
 *                                  cyberResilienceSummary
 *   • Key strengths/improvements→ assessment_executive_summaries via
 *                                  reviewer_get_executive_summary RPC
 *   • Domain rows + commentary  → calculate_csv_domain_scores RPC +
 *                                  csvDomainNarrative() helper (band-aware)
 */
import type jsPDF from "jspdf";
import { PAGE_MARGIN, REPORT_THEME, type RGB } from "./theme";
import { drawRunningHeader, drawPageFooter, drawSectionTitle } from "./pageChrome";
import {
  bandFromScore,
  csvDomainNarrative,
  type MaturityBand,
} from "@/reports/telecom/domainNarratives";
import { overallSummaryLine } from "@/reports/telecom/cyberResilienceSummary";
import {
  INFRASTRUCTURE_COVERAGE_NOTE_PDF,
  INFRASTRUCTURE_PARTIAL_BADGE,
} from "@/reports/telecom/infrastructureCoverageNote";

export interface CsvDomainScoreRow {
  domain_name: string;
  average_score: number | null;
  controls_evaluated?: number | null;
  maturity_level?: string | null;
}

export interface CsvDomainRatingsContext {
  scores: CsvDomainScoreRow[];
  overallBand: MaturityBand | null;
  keyStrengths?: string | null;
  keyAreasForImprovement?: string | null;
  clientOrgName: string;
  methodology?: string | null;
  frameworks?: string[] | null;
  pageNumber?: number;
  /** Phase 2.12 — when true, the Infrastructure Security card commentary is replaced by a coverage note. */
  showInfrastructureCoverageNote?: boolean;
}

/** Maturity band → color (consistent with Page 3 + Page 4). */
const BAND_COLORS: Record<MaturityBand, { rgb: RGB }> = {
  Minimal:   { rgb: [220, 38, 38] },   // red-600
  Adequate:  { rgb: [245, 158, 11] },  // amber-500
  Strong:    { rgb: [22, 163, 74] },   // green-600
  Extensive: { rgb: [37, 99, 235] },   // blue-600
  Unrated:   { rgb: [148, 163, 184] }, // slate-400
};

const BAND_FILL_LEVEL: Record<MaturityBand, number> = {
  Unrated: 0,
  Minimal: 0.25,
  Adequate: 0.5,
  Strong: 0.75,
  Extensive: 1,
};

/** Canonical CSV domain order — approved presentation order. */
export const DOMAIN_ORDER = [
  "Risk Management",
  "Governance",
  "Supply Chain Management",
  "Asset Management",
  "Identity-Centric Security",
  "Security Operations",
  "Data Protection",
  "Infrastructure Security",
];

export function renderCsvDomainRatings(doc: jsPDF, ctx: CsvDomainRatingsContext): void {
  doc.addPage();
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  drawRunningHeader(doc, {
    clientName: ctx.clientOrgName,
    frameworks: ctx.frameworks ?? null,
    methodology: ctx.methodology ?? null,
  });

  // Section 03 continuation — second page of "Cyber Resilience & Domain Ratings".
  let y = drawSectionTitle(doc, {
    sectionNumber: "03",
    title: "Cyber Resilience & Domain Ratings – CSV Core",
    continued: true,
  });

  doc.setTextColor(...REPORT_THEME.textMuted.rgb);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);
  const subtitle =
    "CSV Core represents the Cyber Secure Ventures cybersecurity assessment model, which organizes cybersecurity capabilities into eight operational domains that align security governance, risk management, technology controls, resilience, and compliance activities. The ratings below provide a business-oriented view of cybersecurity maturity and help prioritize remediation efforts across the areas most critical to organizational resilience.";
  const subLines = doc.splitTextToSize(subtitle, pageW - PAGE_MARGIN * 2);
  doc.text(subLines, PAGE_MARGIN, y, { lineHeightFactor: 1.35 } as any);
  y += subLines.length * 13 + 16;

  // ── Top split section ──
  // The CSV Core page is designed as a single-page executive view. Measure the
  // domain grid first, then size the strengths/areas panels against the real
  // remaining space rather than relying on a conservative reserve that can push
  // the grid onto a second page in production.
  const topTop = y;
  const colGap = 16;
  const leftW = (pageW - PAGE_MARGIN * 2 - colGap) * 0.46;
  const rightW = (pageW - PAGE_MARGIN * 2 - colGap) - leftW;
  const innerPanelW = rightW - 14 * 2 - 10; // padX (14) on both sides + 10pt bullet indent

  // Build score lookup before final layout so the page-break decision uses the
  // actual measured grid height, not a fixed worst-case estimate.
  const scoreMap = new Map<string, CsvDomainScoreRow>(
    (ctx.scores ?? []).map((s) => [s.domain_name, s] as const),
  );
  const orderedDomains = [
    ...DOMAIN_ORDER.filter((d) => scoreMap.has(d)),
    ...(ctx.scores ?? [])
      .map((s) => s.domain_name)
      .filter((d) => !DOMAIN_ORDER.includes(d)),
  ];
  const domains = orderedDomains.length > 0 ? orderedDomains : DOMAIN_ORDER;
  const gridLeft = PAGE_MARGIN;
  const gridRight = pageW - PAGE_MARGIN;
  const cols = 4;
  const rows = 2;
  const cardGap = 14;
  const cardW = (gridRight - gridLeft - cardGap * (cols - 1)) / cols;
  const cards = domains.slice(0, cols * rows).map((domainName) => {
    const row = scoreMap.get(domainName);
    const score = row?.average_score ?? null;
    const controls = Number(row?.controls_evaluated ?? 0);
    const band = bandFromScore(score, controls);
    const isInfraCoverageOverride =
      domainName === "Infrastructure Security" &&
      ctx.showInfrastructureCoverageNote === true &&
      band === "Unrated";
    const commentary = isInfraCoverageOverride
      ? INFRASTRUCTURE_COVERAGE_NOTE_PDF
      : csvDomainNarrative(domainName, score, controls);
    return { domainName, band, commentary, partialCoverage: isInfraCoverageOverride };
  });
  const rowHeights = Array.from({ length: rows }, (_, rowIdx) => {
    const measured = cards
      .slice(rowIdx * cols, rowIdx * cols + cols)
      .map((card) => measureDomainCardHeight(doc, { ...card, w: cardW }));
    return Math.max(132, ...measured);
  });

  const panelGap = 10;
  const topToGridGap = 18;
  const gridH = 22 + rowHeights.reduce((sum, h) => sum + h, 0) + cardGap * (rows - 1);
  const footerSafeY = pageH - 52;
  const maxTopH = footerSafeY - topTop - topToGridGap - gridH;

  // Auto-shrink font size so that the full Key Strengths and Key Areas content
  // always fits inside their cards without truncation. Scan from the default
  // 9pt down to 6pt and pick the largest size where both panels fit within
  // the available top region.
  const DEFAULT_PANEL_FONT = 9;
  const MIN_PANEL_FONT = 6;
  let panelFont = DEFAULT_PANEL_FONT;
  let strengthsNeeded = measureBulletPanelHeight(doc, innerPanelW, ctx.keyStrengths, panelFont);
  let areasNeeded = measureBulletPanelHeight(doc, innerPanelW, ctx.keyAreasForImprovement, panelFont);
  while (
    panelFont > MIN_PANEL_FONT &&
    strengthsNeeded + panelGap + areasNeeded > maxTopH
  ) {
    panelFont = Math.round((panelFont - 0.25) * 100) / 100;
    strengthsNeeded = measureBulletPanelHeight(doc, innerPanelW, ctx.keyStrengths, panelFont);
    areasNeeded = measureBulletPanelHeight(doc, innerPanelW, ctx.keyAreasForImprovement, panelFont);
  }

  let strengthsH = Math.max(62, strengthsNeeded);
  let areasH = Math.max(62, areasNeeded);
  if (strengthsH + panelGap + areasH > maxTopH && maxTopH > panelGap + 104) {
    // Even at the minimum font size we still can't fit — fall back to the
    // proportional compression so the grid stays on the same page.
    const availablePanelsH = maxTopH - panelGap;
    const totalNeeded = Math.max(1, strengthsNeeded + areasNeeded);
    const minPanelH = 52;
    strengthsH = Math.min(strengthsH, Math.max(minPanelH, availablePanelsH * (strengthsNeeded / totalNeeded)));
    areasH = Math.min(areasH, availablePanelsH - strengthsH);
    if (areasH < minPanelH) {
      areasH = minPanelH;
      strengthsH = Math.max(minPanelH, availablePanelsH - areasH);
    }
  }
  const topH = strengthsH + panelGap + areasH;

  drawResilienceOverview(doc, {
    x: PAGE_MARGIN,
    y: topTop,
    w: leftW,
    h: topH,
    band: ctx.overallBand,
    clientName: ctx.clientOrgName,
  });

  drawStrengthsAndAreas(doc, {
    x: PAGE_MARGIN + leftW + colGap,
    y: topTop,
    w: rightW,
    h: topH,
    keyStrengths: ctx.keyStrengths,
    keyAreas: ctx.keyAreasForImprovement,
    strengthsH,
    areasH,
    fontSize: panelFont,
  });

  // ── Bottom: CSV Domain Ratings grid ──
  const bottomTop = topTop + topH + topToGridGap;

  doc.setTextColor(...REPORT_THEME.navy.rgb);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text("CSV Cybersecurity Domain Ratings", PAGE_MARGIN, bottomTop);
  doc.setDrawColor(...REPORT_THEME.gold.rgb);
  doc.setLineWidth(1.2);
  doc.line(PAGE_MARGIN, bottomTop + 5, PAGE_MARGIN + 60, bottomTop + 5);

  const gridTop = bottomTop + 22;

  cards.forEach((card, idx) => {
    const r = Math.floor(idx / cols);
    const c = idx % cols;
    const cardX = gridLeft + c * (cardW + cardGap);
    const cardY = gridTop + rowHeights.slice(0, r).reduce((sum, h) => sum + h, 0) + r * cardGap;

    drawDomainCard(doc, {
      x: cardX,
      y: cardY,
      w: cardW,
      h: rowHeights[r],
      ...card,
    });
  });

  drawPageFooter(doc, ctx.pageNumber ?? 5);
}

/* ───────────────── Resilience Overview (left half of split) ───────────────── */

function drawResilienceOverview(
  doc: jsPDF,
  args: {
    x: number;
    y: number;
    w: number;
    h: number;
    band: MaturityBand | null;
    clientName: string;
  },
): void {
  const { x, y, w, h, band, clientName } = args;

  // Card
  doc.setFillColor(...REPORT_THEME.surfaceAlt.rgb);
  doc.setDrawColor(...REPORT_THEME.border.rgb);
  doc.setLineWidth(0.8);
  doc.roundedRect(x, y, w, h, 6, 6, "FD");

  const padX = 16;
  const innerW = w - padX * 2;

  // Title
  doc.setTextColor(...REPORT_THEME.navy.rgb);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Cyber Resilience Overview", x + padX, y + 22);

  // Band pill
  const effective: MaturityBand = band ?? "Unrated";
  const pillLabel = effective === "Unrated" ? "Not Assessed" : effective;
  const pillColor = BAND_COLORS[effective].rgb;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  const pillTextW = doc.getTextWidth(pillLabel);
  const pillPadX = 8;
  const pillH = 16;
  const pillW = pillTextW + pillPadX * 2;
  const pillY = y + 32;
  doc.setFillColor(...pillColor);
  doc.roundedRect(x + padX, pillY, pillW, pillH, pillH / 2, pillH / 2, "F");
  doc.setTextColor(255, 255, 255);
  doc.text(pillLabel, x + padX + pillPadX, pillY + pillH / 2 + 3);

  // 4-segment bar (reuses Page 3 / Page 4 visual language)
  const barTop = pillY + pillH + 16;
  const barH = 8;
  const segCount = 4;
  const segGap = 4;
  const segW = (innerW - segGap * (segCount - 1)) / segCount;
  const filledSegs = Math.round(BAND_FILL_LEVEL[effective] * segCount);

  for (let i = 0; i < segCount; i++) {
    const segX = x + padX + i * (segW + segGap);
    if (i < filledSegs) doc.setFillColor(...pillColor);
    else doc.setFillColor(...REPORT_THEME.border.rgb);
    doc.roundedRect(segX, barTop, segW, barH, barH / 2, barH / 2, "F");
  }

  // Tick labels under bar
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.setTextColor(...REPORT_THEME.textMuted.rgb);
  const tickLabels = ["MIN", "ADQ", "STR", "EXT"];
  tickLabels.forEach((t, i) => {
    const cx = x + padX + i * (segW + segGap) + segW / 2;
    const tw = doc.getTextWidth(t);
    doc.text(t, cx - tw / 2, barTop + barH + 11);
  });

  // 2–3 line summary (deterministic, sourced from overallSummaryLine)
  const summaryY = barTop + barH + 28;
  const summary = overallSummaryLine(effective, clientName);
  doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  const lines = doc.splitTextToSize(summary, innerW) as string[];
  // No truncation — render full summary text.
  doc.text(lines, x + padX, summaryY, { lineHeightFactor: 1.4 } as any);
}

/* ───────────────── Strengths + Areas (right half of split) ───────────────── */

function drawStrengthsAndAreas(
  doc: jsPDF,
  args: {
    x: number;
    y: number;
    w: number;
    h: number;
    keyStrengths?: string | null;
    keyAreas?: string | null;
    strengthsH?: number;
    areasH?: number;
    fontSize?: number;
  },
): void {
  const { x, y, w, h, keyStrengths, keyAreas, strengthsH, areasH, fontSize } = args;
  const gap = 12;
  // Card pair always renders at equal heights matching the taller card
  // (passed via strengthsH/areasH). Both fit fully — no truncation, no overflow.
  const topH = strengthsH ?? (h - gap) / 2;
  const bottomH = areasH ?? (h - gap) / 2;

  drawBulletPanel(doc, {
    x, y, w, h: topH,
    title: "Key Strengths",
    accent: BAND_COLORS.Strong.rgb,
    content: keyStrengths,
    emptyText: "No key strengths recorded yet.",
    fontSize,
  });

  drawBulletPanel(doc, {
    x, y: y + topH + gap, w, h: bottomH,
    title: "Key Areas for Improvement",
    accent: BAND_COLORS.Minimal.rgb,
    content: keyAreas,
    emptyText: "No improvement areas recorded yet.",
    fontSize,
  });
}

/**
 * Measure the height a bullet panel needs to render its full content
 * (no truncation). Mirrors the layout constants used in drawBulletPanel.
 */
function measureBulletPanelHeight(
  doc: jsPDF,
  innerW: number,
  content?: string | null,
  fontSize: number = 9,
): number {
  const titleBlock = 36; // space from top of card to first bullet baseline
  const bottomPad = 14;
  const bulletGap = Math.max(4, Math.round(fontSize * (6 / 9)));
  const lineH = fontSize * (12 / 9);
  const items = parseBulletItems(content);
  if (items.length === 0) {
    return titleBlock + lineH + bottomPad;
  }
  doc.setFont("helvetica", "normal");
  doc.setFontSize(fontSize);
  let total = titleBlock;
  items.forEach((item, idx) => {
    const split = doc.splitTextToSize(item.trim(), innerW) as string[];
    total += split.length * lineH;
    if (idx < items.length - 1) total += bulletGap;
  });
  return total + bottomPad;
}

function drawBulletPanel(
  doc: jsPDF,
  args: {
    x: number;
    y: number;
    w: number;
    h: number;
    title: string;
    accent: RGB;
    content?: string | null;
    emptyText: string;
    fontSize?: number;
  },
): void {
  const { x, y, w, h, title, accent, content, emptyText, fontSize = 9 } = args;

  // Card
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(...REPORT_THEME.border.rgb);
  doc.setLineWidth(0.8);
  doc.roundedRect(x, y, w, h, 6, 6, "FD");

  // Left accent stripe
  doc.setFillColor(...accent);
  doc.rect(x, y, 3, h, "F");

  const padX = 14;
  const innerW = w - padX * 2;

  // Title
  doc.setTextColor(...REPORT_THEME.navy.rgb);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(title, x + padX, y + 18);

  // Bullets — split content into items, max 5
  const items = parseBulletItems(content);
  const bulletStartY = y + 36;
  const bulletGap = Math.max(4, Math.round(fontSize * (6 / 9)));
  const lineH = fontSize * (12 / 9);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(fontSize);

  if (items.length === 0) {
    doc.setTextColor(...REPORT_THEME.textMuted.rgb);
    doc.setFont("helvetica", "italic");
    doc.text(emptyText, x + padX, bulletStartY);
    return;
  }

  // Hard ceiling so text NEVER bleeds outside the card. Card heights are
  // pre-measured so this clip is purely defensive — it guarantees the
  // overflow regression cannot recur even if measurement drifts.
  const bottomCeiling = y + h - 8;
  let cy = bulletStartY;

  for (const item of items) {
    const text = item.trim();
    const split = doc.splitTextToSize(text, innerW - 10) as string[];
    const blockH = split.length * lineH;
    if (cy + blockH > bottomCeiling) {
      // No room for this bullet — stop rather than overflow the card.
      break;
    }
    // Bullet dot
    doc.setFillColor(...accent);
    doc.circle(x + padX + 2, cy - 3, 1.6, "F");

    doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(fontSize);
    doc.text(split, x + padX + 10, cy, { lineHeightFactor: 1.35 } as any);
    cy += blockH + bulletGap;
  }
}

/**
 * Parse free-form text into bullet items.
 * Handles:
 *   - Newline-separated lines
 *   - Lines prefixed with "•", "-", "*", or numbered "1." style
 *   - Falls back to sentence splitting if a single block is provided
 */
function parseBulletItems(content?: string | null): string[] {
  const text = (content ?? "").trim();
  if (!text) return [];

  // Newline split first
  const linesRaw = text.split(/\r?\n+/).map((l) => l.trim()).filter(Boolean);

  if (linesRaw.length > 1) {
    return linesRaw.map(stripBulletPrefix);
  }

  // Single block — try splitting on bullet markers if present
  if (/[•·]/.test(text)) {
    return text.split(/\s*[•·]\s*/).map((s) => s.trim()).filter(Boolean);
  }
  // Else split on sentences (".") — keep up to 5
  const sentences = text
    .split(/(?<=[.!?])\s+(?=[A-Z])/)
    .map((s) => s.trim())
    .filter(Boolean);
  return sentences.length > 1 ? sentences : [text];
}

function stripBulletPrefix(s: string): string {
  return s.replace(/^\s*(?:[-•·*]|\d+\.)\s+/, "").trim();
}

/* ───────────────── Domain card (bottom grid) ───────────────── */

function measureDomainCardHeight(
  doc: jsPDF,
  args: {
    w: number;
    domainName: string;
    commentary: string;
    partialCoverage?: boolean;
  },
): number {
  const padX = 10;
  const innerW = args.w - padX * 2;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.2);
  const nameLines = (doc.splitTextToSize(args.domainName, innerW) as string[]).slice(0, 2);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.8);
  const commentaryLines = doc.splitTextToSize(args.commentary, innerW) as string[];

  const titleH = Math.max(11, nameLines.length * 11);
  const pillH = args.partialCoverage ? 20 : 13;
  const barBlockH = 13;
  const textH = commentaryLines.length * 10.2;
  return 14 + titleH + 8 + pillH + 8 + barBlockH + 10 + textH + 12;
}

function drawDomainCard(
  doc: jsPDF,
  args: {
    x: number;
    y: number;
    w: number;
    h: number;
    domainName: string;
    band: MaturityBand;
    commentary: string;
    /** When true, replace the "Not Assessed" pill with the partial-coverage status. */
    partialCoverage?: boolean;
  },
): void {
  const { x, y, w, h, domainName, band, commentary, partialCoverage } = args;
  const isPartial = partialCoverage === true && band === "Unrated";
  // Amber accent for partial coverage; otherwise standard band color.
  const bandColor: RGB = isPartial ? [217, 119, 6] : BAND_COLORS[band].rgb;

  // Card
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(...REPORT_THEME.border.rgb);
  doc.setLineWidth(0.8);
  doc.roundedRect(x, y, w, h, 5, 5, "FD");

  // (Top accent stripe removed — domain cards use neutral styling; rating
  // is conveyed via the maturity pill and segmented bar only.)

  const padX = 10;
  const innerW = w - padX * 2;

  // Domain name (clamped to 2 lines)
  doc.setTextColor(...REPORT_THEME.navy.rgb);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.2);
  const nameLines = doc.splitTextToSize(domainName, innerW) as string[];
  const nameShown = nameLines.slice(0, 2);
  doc.text(nameShown, x + padX, y + 18, { lineHeightFactor: 1.2 } as any);
  const nameBottom = y + 18 + (nameShown.length - 1) * 11;

  // Maturity pill
  const pillLabel = isPartial
    ? INFRASTRUCTURE_PARTIAL_BADGE
    : band === "Unrated"
      ? "Not Assessed"
      : band;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.2);
  const pillTextW = doc.getTextWidth(pillLabel);
  const pillPadX = 6;
  const pillH = 13;
  const pillW = Math.min(pillTextW + pillPadX * 2, innerW);
  const pillY = nameBottom + 8;
  doc.setFillColor(...bandColor);
  doc.roundedRect(x + padX, pillY, pillW, pillH, pillH / 2, pillH / 2, "F");
  doc.setTextColor(255, 255, 255);
  doc.text(pillLabel, x + padX + pillW / 2, pillY + pillH / 2 + 2.5, { align: "center" });

  // 4-segment bar
  const barTop = pillY + pillH + 8;
  const barH = 5;
  const segCount = 4;
  const segGap = 2.5;
  const segW = (innerW - segGap * (segCount - 1)) / segCount;
  const filledSegs = Math.round(BAND_FILL_LEVEL[band] * segCount);
  for (let i = 0; i < segCount; i++) {
    const segX = x + padX + i * (segW + segGap);
    if (i < filledSegs) doc.setFillColor(...bandColor);
    else doc.setFillColor(...REPORT_THEME.surfaceAlt.rgb);
    doc.roundedRect(segX, barTop, segW, barH, barH / 2, barH / 2, "F");
  }

  // Commentary — render full text; card height is sized to fit.
  const commentaryY = barTop + barH + 14;
  doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.8);
  const lines = doc.splitTextToSize(commentary, innerW) as string[];
  doc.text(lines, x + padX, commentaryY, { lineHeightFactor: 1.3 } as any);
}

